/**
 * services/messages-outbound.js — outbound send helpers (#122).
 *
 * Extracted from messages-service.js so the load + pagination code
 * (normalize, fetch, older-page cursor) stays separate from the
 * optimistic → API → reconcile flow that every send type shares.
 *
 * messages-service.js re-exports every function in here, so existing
 * `import { sendText } from './messages-service.js'` call sites keep
 * working without edits.
 *
 * Every send helper follows the same contract:
 *   1. Validate + resolve the channel phone_id.
 *   2. Build an optimistic row with a client-generated clientId.
 *   3. Append it to the thread store so the bubble renders instantly.
 *   4. Fire the proxy send API with the same clientId as correlation_id.
 *   5. On success, patch the optimistic row with the real wamid.
 *   6. On failure, mark the row FAILED so the user can retry.
 *
 * The correlation_id lets the realtime broadcast reconcile back to the
 * same optimistic row (see realtime.js strategy 1 in _handleMessageInsert).
 */

import * as api from './api.js';
import {
  appendMessage,
  patchMessage,
  getMessages,
  addReaction,
  removeReaction,
} from '../stores/messages.js';
import { updateChat, $phoneIndex, $chats, $serverAccountUnread } from '../stores/chats.js';
import { MESSAGE_STATUS, DIRECTION } from '../shared/constants.js';
import { hapticTap, hapticReact } from './haptics.js';
import { resolveActiveChannel } from './channel-resolve.js';

// _getActiveChannelId moved to services/channel-resolve.js (#120) so
// interactives.js + messages-outbound.js share one definition. Local
// alias kept so call sites read naturally.
const _getActiveChannelId = resolveActiveChannel;

// ── Internal helpers ────────────────────────────────────────────────────────

// Phase B (#136): every outbound message gets a client-generated UUID
// that's used as its correlation_id end-to-end. Stored on the optimistic
// row's id, sent to the proxy, persisted on inbox_messages.correlation_id,
// echoed in the realtime broadcast, and matched O(1) on the client to
// reconcile the optimistic row with the persisted one.
function _newClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // RFC4122-ish fallback for ancient environments.
  return 'cid_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

function _makeOptimistic(type, body, replyToWamid = null, clientId = null) {
  const id = clientId || _newClientId();
  return {
    id,
    clientId:  id, // explicit field so the realtime handler can match on it
    wamid:     null,
    type,
    direction: DIRECTION.OUTBOUND,
    fromMe:    true,
    status:    MESSAGE_STATUS.PENDING,
    timestamp: Math.floor(Date.now() / 1000),
    replyTo:   replyToWamid,
    ...body,
  };
}

/** Extract real wamid from proxy send response */
function _extractWamid(result) {
  return result?.messages?.[0]?.id
      || result?.data?.messages?.[0]?.id
      || result?.message?.queue_id
      || null;
}

// ── Send helpers ────────────────────────────────────────────────────────────

/** Send a text message — optimistic update + API call + reconcile with real wamid */
export async function sendText(phone, text, replyToWamid = null) {
  const channelPhoneId = _getActiveChannelId(phone);

  const optimistic = _makeOptimistic('text', { text: { body: text } }, replyToWamid);
  appendMessage(phone, channelPhoneId, optimistic);
  hapticTap(); // light buzz on send confirm — Android only, no-op elsewhere

  const payload = {
    messaging_product: 'whatsapp',
    recipient_type:    'individual',
    to:                phone,
    type:              'text',
    text:              { body: text },
    _phone_number_id:  channelPhoneId,
    client_id:         optimistic.clientId,  // Phase B (#136) — proxy uses this as correlation_id
  };
  if (replyToWamid) payload.context = { message_id: replyToWamid };

  try {
    const result = await api.sendMessage(payload);
    const realWamid = _extractWamid(result);
    // Reconcile optimistic with real wamid so realtime INSERT can dedupe.
    // Note: clientId stays put — the realtime handler still keys off it
    // when the broadcast lands (which may or may not happen before this
    // patch, depending on network ordering).
    patchMessage(phone, channelPhoneId, optimistic.id, {
      status: MESSAGE_STATUS.SENT,
      wamid: realWamid,
      id: realWamid || optimistic.id,
    });
  } catch (e) {
    console.error('[v2 messages] send failed:', e.message);
    patchMessage(phone, channelPhoneId, optimistic.id, { status: MESSAGE_STATUS.FAILED, error: e.message });
    throw e;
  }
}

/**
 * Send a location session message. Meta's shape:
 *   type: 'location', location: { latitude, longitude, name?, address? }
 *
 * `latitude` and `longitude` are numeric degrees. `name` and `address` are
 * optional display-only strings — WhatsApp renders them below the map pin.
 * Caller is responsible for acquiring the coordinates (geolocation API or
 * manual form). This helper just normalizes the payload and fires the send.
 */
export async function sendLocation(phone, { latitude, longitude, name = '', address = '' } = {}) {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    throw new Error('sendLocation: latitude and longitude must be numbers');
  }
  const channelPhoneId = _getActiveChannelId(phone);

  const optimisticBody = { location: { latitude, longitude, name, address } };
  const optimistic = _makeOptimistic('location', optimisticBody);
  appendMessage(phone, channelPhoneId, optimistic);
  hapticTap();

  try {
    const result = await api.sendMessage({
      messaging_product: 'whatsapp',
      to: phone,
      type: 'location',
      location: {
        latitude,
        longitude,
        ...(name    ? { name }    : {}),
        ...(address ? { address } : {}),
      },
      _phone_number_id: channelPhoneId,
      client_id:        optimistic.clientId, // Phase B (#136)
    });
    const realWamid = _extractWamid(result);
    patchMessage(phone, channelPhoneId, optimistic.id, {
      status: MESSAGE_STATUS.SENT,
      wamid:  realWamid,
      id:     realWamid || optimistic.id,
    });
  } catch (e) {
    patchMessage(phone, channelPhoneId, optimistic.id, { status: MESSAGE_STATUS.FAILED, error: e.message });
    throw e;
  }
}

/**
 * Send a contact card (vcard) session message. Meta's shape:
 *   type: 'contacts', contacts: [{ name, phones[], emails[], org?, addresses?, urls? }]
 *
 * The caller supplies a "simple" shape {name, phone, email, company?} and
 * this helper expands it into Meta's nested contacts[] structure. Multi-
 * contact cards aren't exposed at this layer — one vcard per send is the
 * 99% case for an inbox agent sharing their business card.
 */
export async function sendContact(phone, { firstName, lastName = '', phoneNumber, email = '', company = '', title = '' } = {}) {
  if (!firstName || !phoneNumber) {
    throw new Error('sendContact: firstName and phoneNumber are required');
  }
  const channelPhoneId = _getActiveChannelId(phone);

  const contact = {
    name: {
      formatted_name: (firstName + ' ' + lastName).trim(),
      first_name:     firstName,
      last_name:      lastName || '',
    },
    phones: [{ phone: phoneNumber, type: 'WORK', wa_id: phoneNumber.replace(/[^0-9]/g, '') }],
  };
  if (email) contact.emails = [{ email, type: 'WORK' }];
  if (company || title) {
    contact.org = {
      ...(company ? { company } : {}),
      ...(title   ? { title }   : {}),
    };
  }

  const optimisticBody = { contacts: [contact] };
  const optimistic = _makeOptimistic('contacts', optimisticBody);
  appendMessage(phone, channelPhoneId, optimistic);
  hapticTap();

  try {
    const result = await api.sendMessage({
      messaging_product: 'whatsapp',
      to: phone,
      type: 'contacts',
      contacts: [contact],
      _phone_number_id: channelPhoneId,
      client_id:        optimistic.clientId, // Phase B (#136)
    });
    const realWamid = _extractWamid(result);
    patchMessage(phone, channelPhoneId, optimistic.id, {
      status: MESSAGE_STATUS.SENT,
      wamid:  realWamid,
      id:     realWamid || optimistic.id,
    });
  } catch (e) {
    patchMessage(phone, channelPhoneId, optimistic.id, { status: MESSAGE_STATUS.FAILED, error: e.message });
    throw e;
  }
}

/**
 * Send a media file (image / video / audio / document).
 *
 * @param {string} phone   recipient WhatsApp number
 * @param {File}   file    the picked file
 * @param {string} caption optional caption. Ignored for `audio` (Meta's Cloud
 *                         API doesn't support audio captions). For `image`,
 *                         `video`, and `document` it's passed through to the
 *                         platform and rendered by MessageBubble.
 */
export async function sendMedia(phone, file, caption = '', replyToWamid = null) {
  const channelPhoneId = _getActiveChannelId(phone);

  // Initial type from the OS-reported MIME. Note this is BEFORE
  // _normalizeImageForUpload runs — that helper may rewrite the file
  // (HEIC → JPEG, downscale) and update file.type accordingly.
  const initialType = file.type.startsWith('image/') ? 'image'
                    : file.type.startsWith('video/') ? 'video'
                    : file.type.startsWith('audio/') ? 'audio'
                    : 'document';

  // #194 — iOS HEIC + large-image hotfix. iPhones shoot HEIC by default
  // (Meta Cloud API rejects it), AND the Netlify Functions 6 MB body
  // cap is easy to exceed when a 4 MB photo expands to ~5.4 MB base64
  // inside our JSON envelope — that combo produced an opaque 500 with
  // no body. Re-encoding via canvas solves both: HEIC decodes natively
  // in any modern browser through an <img> tag, and the JPEG re-export
  // shrinks the payload by 4-10×.
  if (initialType === 'image') {
    file = await _normalizeImageForUpload(file);
  }
  const type = initialType;

  // Validate size (WA Cloud API limits) AFTER normalization so a 12 MP
  // iPhone HEIC that downscales to ~800 KB JPEG passes cleanly.
  const MAX = { image: 5e6, video: 16e6, audio: 16e6, document: 100e6 };
  if (file.size > MAX[type]) {
    const limitMB = Math.round(MAX[type] / 1e6);
    throw new Error(`File too large. Max ${limitMB}MB for ${type}.`);
  }

  // Trim the caption once — every downstream use gets the cleaned value.
  // Audio doesn't support captions per Meta Cloud API, so we null it out.
  const trimmedCaption = type === 'audio' ? '' : (caption || '').trim();

  // Optimistic with local blob URL preview. The caption is embedded so it
  // renders instantly under the image/video/doc bubble — when the real
  // message lands via realtime, MessageBubble reads the same caption field.
  const optimisticBody =
      type === 'image'    ? { image:    { link: URL.createObjectURL(file), caption: trimmedCaption } }
    : type === 'video'    ? { video:    { link: URL.createObjectURL(file), caption: trimmedCaption } }
    : type === 'document' ? { document: { filename: file.name, caption: trimmedCaption } }
    :                       { audio:    { filename: file.name } };
  const optimistic = _makeOptimistic(type, optimisticBody, replyToWamid);
  appendMessage(phone, channelPhoneId, optimistic);
  hapticTap();

  // Read as base64 for proxy upload
  const base64 = await new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload  = () => res(reader.result.split(',')[1]);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });

  try {
    const uploadData = await api.uploadMedia({
      file_data: base64,
      file_type: file.type,
      file_name: file.name,
      _phone_number_id: channelPhoneId,
    });
    const mediaId  = uploadData.id || uploadData.media_id;
    // S25 — stable_url is the permanent Supabase Storage public URL.
    // Meta fetches the original from this URL; the PWA renders the
    // bubble from the same URL (or from thumbnail_url for image
    // previews — see below). thumbnail_url is a Supabase render
    // transform producing a ~480px JPEG, served from Supabase's CDN.
    const stableUrl    = uploadData.stable_url    || null;
    const thumbnailUrl = uploadData.thumbnail_url || null;
    if (!mediaId && !stableUrl) throw new Error('Upload returned neither media ID nor stable URL');

    // Build the Meta Cloud API body. Prefer `link` (with stableUrl) when
    // present — Meta fetches from our URL and delivers the original.
    // Fall back to `id` (mediaId) if the Supabase mirror failed
    // (graceful degrade to the legacy path, still works for ~14-30d).
    //
    // thumbnail_url is a PWA-only field: it rides along in the send
    // payload so the proxy stores it in the DB body (MessageBubble
    // reads it for fast-loading previews), but the proxy strips it
    // before forwarding to Meta since the Cloud API rejects unknown
    // fields under image/video/document.
    const useLink = !!stableUrl;
    const captionPart = trimmedCaption ? { caption: trimmedCaption } : {};
    const thumbPart   = thumbnailUrl ? { thumbnail_url: thumbnailUrl } : {};
    const mediaPart =
        type === 'image'    ? (useLink ? { link: stableUrl, ...thumbPart, ...captionPart } : { id: mediaId, ...captionPart })
      : type === 'video'    ? (useLink ? { link: stableUrl, ...thumbPart, ...captionPart } : { id: mediaId, ...captionPart })
      : type === 'document' ? (useLink
          ? { link: stableUrl, filename: file.name, ...captionPart }
          : { id: mediaId, filename: file.name, ...captionPart })
      :                       (useLink ? { link: stableUrl } : { id: mediaId });

    const msgPayload = {
      messaging_product: 'whatsapp',
      to: phone,
      type,
      _phone_number_id: channelPhoneId,
      client_id:        optimistic.clientId, // Phase B (#136)
      [type]: mediaPart,
    };
    // R2a — quote-reply support for media sends. sendText has always had
    // this; sendMedia was missing the hookup so image/video/doc replies
    // from the PWA landed as standalones on the customer's WhatsApp.
    // The proxy's R1 wamid-swap logic handles UUID → real-wamid lookup
    // server-side, so passing the local wamid (whether UUID or real) is
    // enough.
    if (replyToWamid) msgPayload.context = { message_id: replyToWamid };
    const result = await api.sendMessage(msgPayload);
    const realWamid = _extractWamid(result);

    // S25 — patch the optimistic bubble to use the permanent Supabase
    // URL (not the local object-URL blob). The local blob URL is
    // revoked on page unload / GC; by patching to stableUrl we
    // guarantee the bubble renders after a refresh or when the agent
    // switches chats and back. For images, thumbnail_url drives the
    // preview while `link` stays as the full-resolution original.
    const patch = {
      status: MESSAGE_STATUS.SENT,
      wamid: realWamid,
      id: realWamid || optimistic.id,
    };
    if (stableUrl && type !== 'audio') {
      const oldLink = optimistic[type]?.link;
      if (oldLink && oldLink.startsWith('blob:')) {
        try { URL.revokeObjectURL(oldLink); } catch {}
      }
      patch[type] = {
        ...(optimistic[type] || {}),
        link: stableUrl,
        ...(thumbnailUrl ? { thumbnail_url: thumbnailUrl } : {}),
        ...(trimmedCaption ? { caption: trimmedCaption } : {}),
      };
    }
    patchMessage(phone, channelPhoneId, optimistic.id, patch);
  } catch (e) {
    patchMessage(phone, channelPhoneId, optimistic.id, { status: MESSAGE_STATUS.FAILED, error: e.message });
    throw e;
  }
}

/**
 * Mark all unread inbound messages in a thread as read on the WA platform.
 * Customers see the blue tick on their side once this completes.
 *
 * - Filters for inbound messages with status !== 'read' AND a real Meta wamid
 *   (queue_ids and our outbound rows are skipped).
 * - Groups by channel (msg.toPhone for inbound = the phone_number_id that
 *   received the message). Multi-channel threads send one batch per channel.
 * - Fires the proxy mark-read call, then patches local store status to 'read'
 *   on success so subsequent renders + the bubble's tick logic reflect it.
 * - Self-throttling: subsequent calls become no-ops because the filter
 *   excludes already-read messages.
 */
export async function markUnreadAsRead(phone, channelPhoneId = null) {
  // Part 1 — per-wamid Meta mark-read for messages in the loaded thread
  // window. Sends the blue-tick receipt on the customer's side for
  // recently-loaded messages (Meta won't accept mark-read for older ones).
  // S5: getMessages is now composite-keyed. Only operate on the open
  // thread's (phone, channel) pair — other channels' unread rows are
  // handled by their own Thread mount, not ours.
  const messages = getMessages(phone, channelPhoneId);
  const toMark = messages.filter(m =>
    m.direction === DIRECTION.INBOUND &&
    m.status !== MESSAGE_STATUS.READ &&
    typeof m.wamid === 'string' &&
    m.wamid.startsWith('wamid.')
  );

  // All messages in this thread share the same channel by construction,
  // so batch them into a single Meta mark-read call.
  if (toMark.length > 0 && channelPhoneId) {
    const wamids = toMark.map(m => m.wamid);
    try {
      await api.markMessagesRead(wamids, channelPhoneId);
      for (const wamid of wamids) {
        patchMessage(phone, channelPhoneId, wamid, { status: MESSAGE_STATUS.READ });
      }
    } catch (e) {
      console.warn('[v2 messages] mark-read failed for channel', channelPhoneId, e.message);
    }
  }

  // Part 2 — #158 bulk DB mark-read for the ENTIRE (contact, channel)
  // history. Clears the status='delivered' flag on every inbound row that
  // sits outside the current thread window, so the chat row's unread
  // badge drains to 0 instead of staying stuck at the count of old
  // undisplayed messages. Separate from Part 1 because this only touches
  // our Supabase row and does not call Meta.
  //
  // Requires channelPhoneId — without it we can't scope the update to a
  // single (contact, channel) pair and would accidentally mark the other
  // channel's rows as read too. Thread.js passes it through on open.
  if (channelPhoneId) {
    // S2/S3 — Eager optimistic zeroing BEFORE the server round-trip.
    // The user wants the badge + row pill to drop the moment they open
    // a chat, not after a network hop. Snapshot the current unread for
    // this (phone, channel) pair so (a) we can decrement the account-wide
    // badge by the right amount and (b) on final failure, we can roll
    // back the optimistic zero.
    const chatKey = $phoneIndex.get()[`${phone}:${channelPhoneId}`];
    const prevUnread = chatKey ? ($chats.get()[chatKey]?.unread || 0) : 0;
    if (chatKey && prevUnread > 0) {
      // T14 — the local $accountUnread decrement that used to live
      // here is gone. $accountUnread is now derived from $chats, so
      // updateChat(chatKey, {unread: 0}) below automatically drops
      // the local sum by prevUnread and the computed follows.
      updateChat(chatKey, { unread: 0 });
    }

    // S11 — exponential-backoff retry. Network blip shouldn't leave the
    // badge permanently lying. 3 attempts at 0ms / 500ms / 1500ms gives
    // ~2s max latency on a recovered connection; beyond that the user
    // experience is already noticeably broken and a rollback is kinder
    // than silent drift.
    //
    // T11 — server response now carries `account_unread`, the authoritative
    // post-mark-read badge count. We trust that number and overwrite
    // $accountUnread with it; this eliminates the drift that happened
    // when local decrement math (prevUnread subtract) got out of sync
    // with the server's view (FCM push set the badge based on server
    // state, local +1 bumps happen, local -prevUnread mispredicts when
    // realtime skipped the chat-row bump on open threads, etc.). The
    // optimistic zero above still fires immediately for snappy UI;
    // the server number just "settles" things.
    const attempts = [0, 500, 1500];
    let succeeded = false;
    let lastErr = null;
    let serverResp = null;
    for (const delay of attempts) {
      if (delay) await new Promise(r => setTimeout(r, delay));
      try {
        serverResp = await api.markChannelRead(phone, channelPhoneId);
        succeeded = true;
        break;
      } catch (e) {
        lastErr = e;
      }
    }

    if (succeeded) {
      // T11 — pull server-authoritative badge count. Shape is either
      // `{ data: { account_unread } }` (standard proxy wrapper) or
      // `{ account_unread }` directly; handle both.
      //
      // T14 — write to $serverAccountUnread (the floor for paginated-
      // out chats). The derived $accountUnread re-evaluates as
      // max(local_sum, serverCount) and the OS badge listener fires.
      const body = serverResp?.data || serverResp || {};
      const serverBadge = body.account_unread;
      if (typeof serverBadge === 'number' && Number.isFinite(serverBadge)) {
        $serverAccountUnread.set(Math.max(0, serverBadge));
      }
    } else {
      console.warn('[v2 messages] mark-channel-read failed after retries:', lastErr?.message);
      // Roll back the optimistic zero so the user isn't tricked into
      // thinking they've read things they haven't. T14 — updating the
      // chat row alone rehydrates the derived $accountUnread; no
      // direct atom write needed.
      if (chatKey && prevUnread > 0) {
        updateChat(chatKey, { unread: prevUnread });
      }
    }
  }
}

/**
 * Send an emoji reaction to a message.
 *
 * Optimistic flow:
 *   1. Insert reaction into local $reactions store immediately so the bubble
 *      lights up before the network roundtrip completes.
 *   2. Fire the API call.
 *   3. On success, the proxy publishes a broadcast that lands in
 *      _handleMessageInsert → addReaction with the same fromMe=true. The
 *      "one per sender" rule in addReaction collapses the optimistic and
 *      persisted into a single chip.
 *   4. On error, roll back via removeReaction.
 *
 * Empty emoji = removal request — passes through unchanged; the addReaction
 * helper interprets emoji='' as "remove this sender's reaction".
 *
 * `wamid` should be the target message's wamid as known to the local store
 * (NOT necessarily the Meta wamid). The proxy passes it through to the WA
 * API as reaction.message_id.
 */
export async function sendReaction(phone, targetWamid, emoji) {
  const channelPhoneId = _getActiveChannelId(phone);

  // Optimistic local update
  const optimistic = {
    emoji,
    fromMe:    true,
    fromPhone: null, // identity is implicit when fromMe is true
    timestamp: Math.floor(Date.now() / 1000),
    wamid:     null, // optimistic — real wamid arrives via broadcast
  };
  addReaction(phone, channelPhoneId, targetWamid, optimistic);
  hapticReact(); // light double-tap buzz

  try {
    await api.sendMessage({
      messaging_product: 'whatsapp',
      recipient_type:    'individual',
      to:                phone,
      type:              'reaction',
      reaction:          { message_id: targetWamid, emoji },
      _phone_number_id:  channelPhoneId,
      // Phase B (#136) — reactions get a fresh correlation_id too. There's
      // no optimistic message row to attach it to (reactions are stored on
      // the $reactions store, not $threads), but the proxy still needs an
      // id for #130 status correlation. The client doesn't currently match
      // back via this id (reactions reconcile via target wamid), but
      // keeping it consistent across all send paths makes the proxy code
      // simpler and lets us add reaction-broadcast dedup later if needed.
      client_id:         _newClientId(),
    });
  } catch (e) {
    console.error('[v2 messages] reaction send failed:', e.message);
    // Roll back the optimistic
    removeReaction(phone, channelPhoneId, targetWamid, /*fromMe*/ true);
    throw e;
  }
}

// ── #194 — Image normalization for upload ──────────────────────────────────
//
// Two related problems this helper solves:
//
//   1. iOS HEIC. iPhone's default photo format is image/heic. WhatsApp Cloud
//      API explicitly rejects it (jpeg / png / webp only) and returns a
//      generic 400 the proxy then surfaces with a confusing error.
//
//   2. Netlify Functions request-body cap (6 MB). We base64-encode the file
//      into a JSON envelope before POSTing to /media/upload — a 4-5 MB
//      photo blows up to ~5.4-6.7 MB JSON and the function rejects with
//      an empty 500 (no body to parse, hence the "Request failed (500)"
//      the user saw).
//
// Re-encoding through a canvas fixes both: the browser decodes whatever
// format it understands (Safari handles HEIC natively via <img>), we
// downscale to a max edge of MAX_DIM, and re-export as JPEG @ 0.85
// quality. Result: 12-MP iPhone photos land at ~600-1200 KB and Meta
// happily accepts them. Already-small JPEGs/PNGs/WebPs skip the round-trip.
const MAX_DIM      = 2048;            // longest edge after downscale
const TRIGGER_SIZE = 1.5 * 1024 * 1024; // anything bigger gets re-encoded

async function _normalizeImageForUpload(file) {
  if (!file || !file.type?.startsWith('image/')) return file;

  // GIFs would lose animation through canvas — let WA reject them upstream
  // if too big. SVG decoding is browser-dependent; same call.
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;

  const isHeic   = /image\/heic|image\/heif/i.test(file.type)
                 || /\.(heic|heif)$/i.test(file.name || '');
  const isLarge  = file.size > TRIGGER_SIZE;
  if (!isHeic && !isLarge) return file;

  let dataUrl;
  try {
    dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload  = () => resolve(r.result);
      r.onerror = () => reject(new Error('Could not read image'));
      r.readAsDataURL(file);
    });
  } catch (e) {
    console.warn('[v2 messages] image read failed, sending raw:', e.message);
    return file;
  }

  let img;
  try {
    img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload  = () => resolve(i);
      i.onerror = () => reject(new Error('Could not decode image — your browser may not support this format'));
      i.src = dataUrl;
    });
  } catch (e) {
    // Safari decodes HEIC natively. If we land here on iOS HEIC the user
    // is on an unusual browser combo — let the original file through and
    // surface a clearer error if Meta then rejects.
    console.warn('[v2 messages] image decode failed:', e.message);
    if (isHeic) throw new Error('HEIC image could not be converted on this device — try sharing a JPEG');
    return file;
  }

  let width  = img.naturalWidth  || img.width;
  let height = img.naturalHeight || img.height;
  if (width <= 0 || height <= 0) return file;

  if (width > MAX_DIM || height > MAX_DIM) {
    const scale = Math.min(MAX_DIM / width, MAX_DIM / height);
    width  = Math.round(width  * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width  = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.85));
  if (!blob) {
    console.warn('[v2 messages] canvas.toBlob returned null, sending raw');
    return file;
  }

  const newName = (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
  const out     = new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  return out;
}
